'use client';

import { Footer } from '@/components/public/footer';
import { NavPreview } from '@/components/public/nav-preview';
import { useEffect, useState } from 'react';
import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';
import { MapPin, Car, Train, Plane, ChevronDown, Navigation, ExternalLink, Clock } from 'lucide-react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useFaqCount } from '@/lib/faq-hooks';

const GOOGLE_MAPS_LINK =
  'https://www.google.com/maps/dir//Daniel+Castle,+DJ131,+520064+T%C4%83li%C8%99oara/@46.1036085,25.584549,265m/data=!3m1!1e3!4m9!4m8!1m0!1m5!1m1!1s0x474b35841478dc35:0x9f6219daf1b86b10!2m2!1d25.5865345!2d46.1037999!3e0?entry=ttu';

const GOOGLE_MAPS_EMBED =
  'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1325.0!2d25.5865345!3d46.1037999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x474b35841478dc35%3A0x9f6219daf1b86b10!2sDaniel%20Castle!5e1!3m2!1sen!2sro!4v1';

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function HowToFindUsPage() {
  const t = useTranslations('design.howToFindUs');
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const faqCount = useFaqCount('design.howToFindUs.faq');

  useEffect(() => {
    const t = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  useScrollReveal();

  return (
    <main className="min-h-screen bg-white text-gray-900">
      <style jsx global>{`
        @keyframes breatheSlow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .image-breathe-slow {
          animation: breatheSlow 10s ease-in-out infinite;
        }
      `}</style>

      <NavPreview />

      {/* ━━━ HERO ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="relative h-[70vh] md:h-[80vh] w-full overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src="/images/how-to-find-us-gate2.webp"
            alt={t('hero.imageAlt')}
            fill
            className="object-cover image-breathe-slow"
            priority
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />

        <div className={`relative h-full flex flex-col items-center justify-center px-6 text-center transition-all duration-[2000ms] ease-out ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h1
            className="font-poppins text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight"
            style={{ textShadow: '2px 2px 30px rgba(0,0,0,0.5)' }}
          >
            {t('hero.titleLine1')}
          </h1>
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-[2px] w-20 bg-white/50" style={{ animation: 'pulse 3s ease-in-out infinite' }} />
            <p
              className="text-base md:text-lg uppercase tracking-[0.3em] text-white/90 [text-shadow:rgba(0,0,0,1)_1px_1px_3px] md:[text-shadow:rgb(0,0,0)_1.5px_1.5px_4px]"
              style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}
            >
              {t('hero.titleLine2')}
            </p>
            <div className="h-[2px] w-20 bg-white/50" style={{ animation: 'pulse 3s ease-in-out infinite', animationDelay: '1.5s' }} />
          </div>
          <p
            className="text-3xl md:text-4xl text-white/95 w-fit max-w-3xl mx-auto font-light [text-shadow:rgba(0,0,0,1)_1.5px_1.5px_4px] md:[text-shadow:rgb(0,0,0)_2px_2px_6px]"
            style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}
          >
            {t('hero.address')}
          </p>

          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50">
            <span className="text-xs uppercase tracking-widest">{t('hero.directions')}</span>
            <ChevronDown className="w-5 h-5 animate-bounce" />
          </div>
        </div>
      </section>

      {/* ━━━ ADDRESS + MAP ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap">
        <div className="max-w-7xl mx-auto design-content-pad">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
            {/* Address card */}
            <div className="lg:col-span-2 observe-me slide-left">
              <div className="bg-[#faf8f6] rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#801515]/10 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[#801515]" />
                  </div>
                  <h2 className="font-poppins text-2xl font-bold text-gray-900">{t('addressCard.heading')}</h2>
                </div>

                <address className="not-italic text-lg text-gray-700 leading-relaxed mb-6 space-y-1">
                  <p className="font-semibold text-gray-900 text-xl">{t('addressCard.name')}</p>
                  <p>{t('addressCard.street')}</p>
                  <p>{t('addressCard.county')}</p>
                  <p>{t('addressCard.country')}</p>
                </address>

                <div className="text-sm text-gray-400 mb-8 font-mono">
                  46.1040&deg; N, 25.5865&deg; E
                </div>

                <a
                  href={GOOGLE_MAPS_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-[#801515] hover:bg-[#601010] text-white px-6 py-3.5 rounded-full font-medium transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 group w-full justify-center"
                >
                  <Navigation className="w-5 h-5" />
                  {t('addressCard.getDirections')}
                  <ExternalLink className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity" />
                </a>
              </div>
            </div>

            {/* Map embed */}
            <div className="lg:col-span-3 observe-me slide-right">
              <div className="rounded-3xl overflow-hidden shadow-lg border border-gray-100 bg-gray-100">
                <iframe
                  src={GOOGLE_MAPS_EMBED}
                  width="100%"
                  height="450"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={t('map.iframeTitle')}
                  className="w-full h-[350px] md:h-[450px] lg:h-[480px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ BY CAR ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap bg-[#faf8f6] rounded-3xl overflow-hidden">
        <div className="max-w-5xl mx-auto design-content-pad lg:py-20">
          <div className="text-center mb-14 observe-me slide-up">
            <div className="w-16 h-16 rounded-full bg-[#801515]/10 flex items-center justify-center mx-auto mb-5">
              <Car className="w-8 h-8 text-[#801515]" />
            </div>
            <h2 className="font-poppins text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
              {t('byCar.heading')}
            </h2>
            <p className="text-lg text-gray-500">{t('byCar.subtitle')}</p>
          </div>

          <div className="space-y-6">
            {(['route1', 'route2', 'route3'] as const).map((key, i) => (
              <div
                key={key}
                className="observe-me slide-up bg-white rounded-2xl shadow-md p-6 md:p-8 hover:shadow-xl transition-shadow duration-300 border border-gray-100"
                style={{ animationDelay: `${i * 0.15}s` }}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="md:w-56 flex-shrink-0">
                    <p className="text-sm text-[#801515] uppercase tracking-wider font-medium mb-1">{t('byCar.fromLabel')}</p>
                    <p className="font-poppins text-xl font-bold text-gray-900">{t(`byCar.${key}.from`)}</p>
                  </div>
                  <div className="hidden md:block w-px h-12 bg-gray-200" />
                  <div className="flex-1">
                    <p className="text-gray-700 text-lg leading-relaxed">{t(`byCar.${key}.route`)}</p>
                    {t.has(`byCar.${key}.note`) && (
                      <p className="text-sm text-gray-400 mt-2 italic">{t(`byCar.${key}.note`)}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ BY TRAIN ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap">
        <div className="max-w-5xl mx-auto design-content-pad">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="observe-me slide-left">
              <div className="w-16 h-16 rounded-full bg-[#801515]/10 flex items-center justify-center mb-5">
                <Train className="w-8 h-8 text-[#801515]" />
              </div>
              <h2 className="font-poppins text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                {t('byTrain.heading')}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                {t.rich('byTrain.description', { strong: (chunks) => <strong>{chunks}</strong> })}
              </p>
              <p className="text-gray-500 leading-relaxed mb-8">
                {t('byTrain.timetableNote')}
              </p>
              <a
                href={t('byTrain.scheduleUrl')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#801515] hover:text-[#601010] font-medium transition-colors duration-300 group"
              >
                {t('byTrain.checkSchedules')}
                <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>

            <div className="observe-me slide-right">
              <div className="bg-[#faf8f6] rounded-3xl p-8 md:p-10 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#801515]/10 flex items-center justify-center mt-1">
                    <Navigation className="w-5 h-5 text-[#801515]" />
                  </div>
                  <div>
                    <p className="font-poppins font-semibold text-gray-900 text-lg mb-2">
                      {t('byTrain.routeLabel')}
                    </p>
                    <p className="text-gray-500 mb-4">
                      {t.rich('byTrain.driveTime', { strong: (chunks) => <strong className="text-gray-700">{chunks}</strong> })}
                    </p>
                    <p className="text-sm text-gray-400">
                      {t('byTrain.carRental')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ AIRPORTS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap bg-[#1a1010] text-white rounded-3xl overflow-hidden">
        <div className="max-w-5xl mx-auto design-content-pad lg:py-20">
          <div className="text-center mb-14 observe-me slide-up">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-5">
              <Plane className="w-8 h-8 text-white/80" />
            </div>
            <h2 className="font-poppins text-3xl md:text-4xl lg:text-5xl font-bold mb-3">
              {t('airports.heading')}
            </h2>
            <p className="text-lg text-white/50">{t('airports.subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {(['brasov', 'bucharest', 'sibiu'] as const).map((key, i) => {
              const airportUrls: Record<string, { url: string; carRentalUrl: string | null }> = {
                brasov: { url: 'https://brasovairport.ro/', carRentalUrl: 'https://brasovairport.ro/pasageri/#parcare' },
                bucharest: { url: 'https://bucharestairports.ro/', carRentalUrl: null },
                sibiu: { url: 'https://www.sibiuairport.ro/', carRentalUrl: null },
              };
              const { url, carRentalUrl } = airportUrls[key];
              return (
                <div
                  key={key}
                  className="observe-me slide-up bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                  style={{ animationDelay: `${i * 0.15}s` }}
                >
                  {/* Drive time badge */}
                  <div className="flex items-center gap-2 mb-5">
                    <div className="flex items-center gap-1.5 bg-[#801515] px-3 py-1.5 rounded-full">
                      <Clock className="w-3.5 h-3.5" />
                      <span className="text-sm font-semibold">{t(`airports.${key}.driveTime`)}</span>
                    </div>
                    <span className="text-xs text-white/40">{t('airports.byCar')}</span>
                  </div>

                  <h3 className="font-poppins text-xl font-bold text-white mb-1">
                    {t(`airports.${key}.city`)}
                  </h3>
                  <p className="text-white/50 text-sm mb-5 leading-relaxed">
                    {t(`airports.${key}.name`)}
                  </p>

                  <div className="space-y-2">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors duration-200"
                    >
                      <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                      {t('airports.websiteLink')}
                    </a>
                    {carRentalUrl && (
                      <a
                        href={carRentalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors duration-200"
                      >
                        <Car className="w-3.5 h-3.5 flex-shrink-0" />
                        {t('airports.carRentalLink')}
                      </a>
                    )}
                  </div>

                  {/* Visual route indicator */}
                  <div className="mt-6 flex items-center gap-2 text-white/20">
                    <Plane className="w-4 h-4" />
                    <div className="flex-1 border-t border-dashed border-white/20 relative">
                      <Car className="w-3.5 h-3.5 absolute -top-[7px] left-1/2 -translate-x-1/2 text-white/30" />
                    </div>
                    <MapPin className="w-4 h-4 text-[#801515]" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ━━━ FAQ ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap bg-[#faf8f6] rounded-3xl overflow-hidden">
        <div className="max-w-3xl mx-auto design-content-pad lg:py-20">
          <div className="text-center mb-14 observe-me slide-up">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6">
              <span className="text-[#801515]">{t('faq.heading')}</span>
              <br />
              <span className="text-gray-800 text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('faq.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mx-auto" />
          </div>

          <div className="space-y-4">
            {Array.from({ length: faqCount }, (_, idx) => idx + 1).map((n, i) => (
              <div
                key={n}
                className="observe-me slide-up"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 p-6 md:p-8 text-left group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-poppins text-lg md:text-xl font-bold text-gray-900 leading-tight pr-4">
                      {t(`faq.q${n}`)}
                    </h3>
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full bg-[#801515]/10 flex items-center justify-center transition-transform duration-300 ${expandedFaq === i ? 'rotate-180' : ''}`}>
                      <ChevronDown className="w-5 h-5 text-[#801515]" />
                    </div>
                  </div>

                  {expandedFaq === i && (
                    <p className="text-gray-600 text-base md:text-lg leading-relaxed mt-5 pt-5 border-t border-gray-100">
                      {t(`faq.a${n}`)}
                    </p>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
